import { filterFieldSupportsMultipleOperands, filterFieldToString, isFilterFieldNumeric, parseFilterField, type FilterField } from "@/lib/domain/FilterField";
import { filterOperatorToString, isFilterOperatorNumeric, parseFilterOperator, type FilterOperator } from "@/lib/domain/FilterOperator";

export interface SearchQuery {
	filters: Filter[];
	segments: string[];
}

export interface Filter {
	field: FilterField;
	operator: FilterOperator;
	operands: string[];
}

export const getSearchStringFromSearchQuery = (searchQuery: SearchQuery): string => {
	const segments = searchQuery.segments;

	const filters = searchQuery.filters.filter(x => x.operands.length > 0).map(filter => {
		const field = filterFieldToString(filter.field);
		const operator = filterOperatorToString(filter.operator);
		const operands = filterFieldSupportsMultipleOperands(filter.field)
			? filter.operands.map(value => value.toString()).join(",")
			: filter.operands[0] ? filter.operands[0] : "";

		return field + operator + operands;
	});

	return segments.concat(filters).join(" ");
};

const filterRegex = /([a-zA-Z]+?)(>=|<=|>|<|!=|=)([a-zA-Z0-9,]*)/;
const segmentRegex = /[^a-zA-Z0-9]/g;
const characterRegex = /[^a-zA-Z0-9><!=\s,]/;
const whitespaceRegex = /\s+/;

export const getSearchQueryFromSearchString = (searchQuery: string): SearchQuery | null => {
	let refinedSearchQuery = searchQuery.replace(whitespaceRegex, " ");
	refinedSearchQuery = refinedSearchQuery.replace(characterRegex, "");

	let remainingSearchTerms = refinedSearchQuery ?? "";
	const filters: Filter[] = [];

	while (true) {
		const match = remainingSearchTerms.match(filterRegex);
		if (!match || match.index === undefined) {
			break;
		}

		remainingSearchTerms =
			remainingSearchTerms.substring(0, match.index) +
			remainingSearchTerms.substring(match.index + match[0].length);

		const field = parseFilterField(match[1]);
		if (field === null) {
			continue;
		}

		let operator = parseFilterOperator(match[2]);
		if (operator === null) {
			continue;
		}

		if (!isFilterFieldNumeric(field) && isFilterOperatorNumeric(operator)) {
			operator = "EqualTo";
		}

		const operands = match[3].split(",").filter(operand => operand.length > 0);
		if (operands.length < 1) {
			continue;
		}

		if (isFilterFieldNumeric(field) && isNaN(parseInt(operands[0]))) {
			continue;
		}

		if (operands.length > 1 && !filterFieldSupportsMultipleOperands(field)) {
			continue;
		}

		const filter: Filter = {
			field: field,
			operator: operator,
			operands: operands
		};

		filters.push(filter);
	}

	const segments = remainingSearchTerms
		.split(" ")
		.map(x => x.replace(segmentRegex, ""))
		.map((x, i) => ({x: x, i: i}))
		.filter(y => y.i === 0 || y.x.length > 1)
		.map(y => y.x)
		.filter(x => !!x);

	return {
		filters: filters,
		segments: segments
	};
};
